import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

interface NavItem {
  id: string;
  label: string;
  path: string;
}

interface NavigationProps {
  isLightboxOpen?: boolean;
}

const navItems: NavItem[] = [
  { id: 'home', label: 'Home', path: '/' },
  { id: 'memories', label: 'Our Memories', path: '/our-memories' },
  // { id: 'story', label: 'Our Story', path: '/story' },
  { id: 'playlist', label: 'Playlist', path: '/playlist' },
  { id: 'photocards', label: 'Photocards', path: '/photocards' },
  { id: 'for-zaria', label: 'For Zaria', path: '/for-zaria' },
  { id: 'valentines-2026', label: "Valentine's 2026", path: '/valentines-2026' },
];

const Navigation: React.FC<NavigationProps> = ({ isLightboxOpen = false }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <>
      <MenuButton 
        onClick={toggleMenu} 
        isOpen={isMenuOpen}
        isLightboxOpen={isLightboxOpen}
      >
        MENU
      </MenuButton>
      
      <AnimatePresence>
        {isMenuOpen && (
          <MenuOverlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <MenuContent
              initial={{ y: -50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -50, opacity: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <MenuHeader>
                <MenuLogo as={Link} to="/">
                  Our Anniversary
                </MenuLogo>
                <CloseButton onClick={toggleMenu}>×</CloseButton>
              </MenuHeader>
              
              <MenuItems>
                {navItems.map((item) => (
                  <MenuItemLink 
                    key={item.id}
                    to={item.path}
                    onClick={closeMenu}
                  >
                    {item.label}
                  </MenuItemLink>
                ))}
              </MenuItems>
            </MenuContent>
          </MenuOverlay>
        )}
      </AnimatePresence>
    </>
  );
};

const MenuButton = styled.button<{ isOpen: boolean; isLightboxOpen?: boolean }>`
  position: fixed;
  top: 2rem;
  right: 2rem;
  z-index: 1001;
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.light};
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent1};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    top: 1rem;
    right: 1rem;
    font-size: 0.9rem;
    
    /* Hide menu button on mobile when lightbox is open */
    ${({ isLightboxOpen }) => isLightboxOpen && `
      opacity: 0;
      pointer-events: none;
      transform: translateY(-20px);
    `}
  }
`;

const MenuOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MenuContent = styled(motion.div)`
  background-color: transparent;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  text-align: center;
`;

const MenuHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  position: absolute;
  top: 2rem;
  left: 2rem;
  right: 2rem;
`;

const MenuLogo = styled.div`
  font-size: 1.2rem;
  font-weight: 600;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.light};
  text-transform: uppercase;
  letter-spacing: 0.1em;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.light};
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent1};
  }
`;

const MenuItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  margin-top: 4rem;
`;

const MenuItemLink = styled(Link)`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.light};
  text-decoration: none;
  font-size: 2.5rem;
  font-weight: 300;
  padding: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  &:hover {
    color: ${({ theme }) => theme.colors.accent1};
    transform: translateX(10px);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 2rem;
  }
`;

export default Navigation; 